import React, {
  Fragment,
  useState,
  useEffect,
  forwardRef,
  useRef,
  useContext,
  useMemo,
} from "react";
import clsx from "clsx";
import moment from "moment";
import {
  Line,
  Tooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Area,
  ComposedChart,
} from "recharts";

import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";
import Legend from "components/QueryBuilder/Elements/Legend";

import {
  MAIN_COLORS,
  selectFormat,
  StandardTooltip,
} from "components/Utils/Utils.js";

import {
  formatQueryMetric,
  formatDate,
} from "components/QueryBuilder/Utils/QueryBuilderUtils";
import QueryBuilderXAxis from "components/QueryBuilder/Elements/QueryBuilderXAxis";
import {
  containsToolTipData,
  excludePreditionMetrics,
  extractPredictionData,
  isDataKeyAPrediction,
  metricIsNotAPrediction,
} from "components/Utils/ChartsDataUtils";

import { formatDataForChart } from "components/Utils/ChartsDataUtils";
import QueryBuilderTooltipPrediction from "components/QueryBuilder/Visualization/tooltips/QueryBuilderTooltipPrediction";
import QueryBuilderTooltipMetric from "components/QueryBuilder/Visualization/tooltips/QueryBuilderTooltipMetric";

const QueryBuilderLineChart = forwardRef((props, ref) => {
  const { state: globalState } = useContext(QueryBuilderContext);

  // Trended Views
  const { chartData, all_dim_keys, dimensionsMap } = useMemo(
    () => formatDataForChart(props.dataGroupedByDate, true),
    [props.dataGroupedByDate]
  );

  const [singleTooltipData, setTooltipData] = useState(null);
  const [seriesFilter, setSeriesFilter] = useState(null);
  const [state, setState] = useState({
    colors: {},
    legendData: [],
  });

  const refChart = useRef(null);

  const [counterLoadMore, setCounterLoadMore] = useState(1);
  const LOAD_MORE_VALUE = 50;

  const { legendData, colors } = state;

  useEffect(() => {
    const legendData = [
      {
        name: "metrics",
        friendlyName: "Metrics",
        value: [
          {
            name: "select_all",
            friendlyName: "Select all",
            color: "#8a9ca3",
            checked: true,
          },
        ],
      },
    ];

    Object.keys(props.metricMap).forEach((metric) =>
      legendData[0].value.push({
        name: props.metricMap[metric].name,
        friendlyName: props.metricMap[metric].friendlyName,
        color: "#38474d",
        checked: true,
      })
    );

    const colors = {};
    props.metrics.forEach((metric, j) => {
      const metrics = [
        {
          name: "select_all",
          friendlyName: "Select all",
          color: "#8a9ca3",
          checked: true,
        },
      ];

      if (all_dim_keys && all_dim_keys.length) {
        all_dim_keys.forEach((dim_key, i) => {
          colors[metric + ":" + dim_key] =
            MAIN_COLORS[(j * all_dim_keys.length + i) % 20];
          metrics.push({
            name: metric + ":" + dim_key,
            friendlyName: dim_key
              .split(":")
              .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
              .join(" | "),
            color: colors[metric + ":" + dim_key],
            checked: true,
          });
        });

        legendData.push({
          name: props.metricMap[metric].name,
          friendlyName: props.metricMap[metric].friendlyName,
          value: metrics,
        });
      } else {
        colors[metric] = MAIN_COLORS[j % 20];
        legendData[0].value.find((d) => d.name === metric).color =
          colors[metric];
      }
    });

    setState({
      ...state,
      colors,
      legendData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLines = () => {
    let lines = [];
    props.metrics
      // ignores the metrics that comes from the prediction
      .filter(excludePreditionMetrics)
      .forEach((metric, j) => {
        if (all_dim_keys && all_dim_keys.length) {
          // metrics + dimensions
          all_dim_keys.forEach((dim_key, i) => {
            if (
              !seriesFilter ||
              (seriesFilter && seriesFilter.includes(metric + ":" + dim_key))
            ) {
              lines.push(
                <Line
                  key={`line_${metric + ":" + dim_key}`}
                  type="monotone"
                  dataKey={metric + ":" + dim_key}
                  stroke={`${colors[metric + ":" + dim_key]}`}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  activeDot={{
                    onMouseOver: (event, payload) => setTooltipData(payload),
                    onMouseOut: () => setTooltipData(null),
                  }}
                />
              );

              // should we display predictions for dimensions?
              if (globalState.loadPredictions) {
                // area for the Max and Min values
                globalState.viewUncertainty &&
                  lines.push(
                    <Area
                      type="linear"
                      key={metric + ":" + dim_key + ".predMinMax"}
                      dataKey={metric + ":" + dim_key + ".predMinMax"}
                      stroke="none"
                      fill={`${colors[metric + ":" + dim_key]}`}
                      opacity=".5"
                    />
                  );
                lines.push(
                  // line for the predicted value
                  <Line
                    key={`line_${metric + ":" + dim_key + ".pred"}`}
                    dataKey={metric + ":" + dim_key + ".pred"}
                    stroke={`${colors[metric + ":" + dim_key]}`}
                    strokeDasharray="3 3 3 3"
                    strokeWidth={2}
                    type="linear"
                    activeDot={{
                      onMouseOver: (event, payload) => setTooltipData(payload),
                      onMouseOut: () => setTooltipData(null),
                    }}
                  />
                );
              }
            }
          });
        } else {
          // only metrics
          if (
            !seriesFilter ||
            (seriesFilter && seriesFilter.includes(metric))
          ) {
            // line for the value
            lines.push(
              <Line
                key={`line_${metric}`}
                type="monotone"
                dataKey={metric}
                stroke={`${colors[metric]}`}
                strokeWidth={2}
                dot={false}
                activeDot={{
                  onMouseOver: (event, payload) => setTooltipData(payload),
                  onMouseOut: () => setTooltipData(null),
                }}
              />
            );

            // should we display predictions?
            if (globalState.loadPredictions) {
              // area for the Max and Min values
              globalState.viewUncertainty &&
                lines.push(
                  <Area
                    type="linear"
                    key={metric + ".predMinMax"}
                    dataKey={metric + ".predMinMax"}
                    stroke="none"
                    fill={`${colors[metric]}`}
                    opacity=".5"
                  />
                );

              // line for the predicted value
              lines.push(
                <Line
                  key={`line_${metric + ".pred"}`}
                  dataKey={metric + ".pred"}
                  stroke={`${colors[metric]}`}
                  strokeDasharray="3 3 3 3"
                  strokeWidth={2}
                  type="linear"
                  activeDot={{
                    onMouseOver: (event, payload) => setTooltipData(payload),
                    onMouseOut: () => setTooltipData(null),
                  }}
                />
              );
            }
          }
        }
      });

    return lines;
  };

  const allLines = renderLines();
  const numberOfLines = LOAD_MORE_VALUE * counterLoadMore;

  const onChangeSelection = (selection) => {
    const checkedMetrics = selection.metrics
      .slice(1)
      .filter((d) => d.checked)
      .map((d) => d.name);
    const _seriesFilter = checkedMetrics.slice(0);
    checkedMetrics.forEach(
      (m) =>
        selection[m] &&
        selection[m]
          .slice(1)
          .forEach((d) => d.checked && _seriesFilter.push(d.name))
    );
    setSeriesFilter(_seriesFilter);
  };

  const chartWidth = refChart.current ? refChart.current.offsetWidth / 43 : "";
  return (
    <div className="row">
      <div
        ref={refChart}
        className="col-10 query-view-charts query-view-border pt-4 pb-4 pl-3 pr-3"
      >
        {allLines.length > numberOfLines ? (
          <div className="query-view-load-more mr-3 d-flex align-items-baseline">
            <span>{numberOfLines} items displayed</span>
            <button
              className="btn btn-link nav__item nav__item--sm hidden-md-down ml-3"
              onClick={() => setCounterLoadMore(counterLoadMore + 1)}
            >
              Load more
            </button>
          </div>
        ) : (
          ""
        )}

        <ResponsiveContainer height={500} width="100%">
          <ComposedChart ref={ref} data={chartData}>
            <CartesianGrid
              stroke="#FEE5DE"
              strokeDasharray="3 3"
              vertical={false}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              mirror={true}
              tickFormatter={(d) =>
                props.formatType ? selectFormat(props.formatType, d) : d
              }
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              interval={
                chartData.length < chartWidth
                  ? 0
                  : Math.round(chartData.length / chartWidth)
              }
              minTickGap={20}
              //tickCount={10}
              padding={{ left: 20, right: 20 }}
              tick={<QueryBuilderXAxis granularity={props.granularity} />}
            />
            <Tooltip
              cursor={{ fill: "#eee", opacity: 0.5 }}
              content={
                <StandardTooltip
                  render={() => {
                    if (singleTooltipData) {
                      const tooltipData = {
                        date: singleTooltipData.payload.date,
                        [singleTooltipData.dataKey]: singleTooltipData.value,
                      };

                      const isPrediction = isDataKeyAPrediction(
                        singleTooltipData.dataKey
                      );

                      const currentDatePoint = tooltipData["date"];

                      const renderItem = (
                        index,
                        parent,
                        isPrediction = false
                      ) => {
                        const dim = props.dimensions[index];
                        return dim.values.map((dim_key, i) => {
                          const dimFriendlyName = dimensionsMap
                            .map((dimensions) =>
                              dimensions.find(
                                (dim) => dim.name && dim.name === dim_key
                              )
                            )
                            .filter((o) => o !== undefined)[0]?.friendlyName;
                          const full_key = parent + ":" + dimFriendlyName;
                          const color = colors[full_key];

                          return containsToolTipData(
                            full_key,
                            seriesFilter,
                            tooltipData
                          ) ? (
                            index < props.dimensions.length - 1 ? (
                              <Fragment key={full_key}>
                                <div
                                  className="standard-tooltip__item"
                                  style={{
                                    marginLeft: (index + 1) * 15 + "px",
                                  }}
                                >
                                  <span
                                    className={clsx(
                                      "standard-tooltip__key",
                                      "text-capitalize"
                                    )}
                                  >
                                    {dimFriendlyName}
                                  </span>
                                  <br />
                                </div>
                                {renderItem(index + 1, full_key)}
                              </Fragment>
                            ) : (
                              <div
                                className="standard-tooltip__item"
                                key={full_key}
                                style={{ marginLeft: (index + 1) * 15 + "px" }}
                              >
                                <span
                                  className="standard-tooltip__icon"
                                  style={{ background: color }}
                                />
                                <span
                                  className={clsx(
                                    "standard-tooltip__key",
                                    "text-capitalize"
                                  )}
                                  style={{ color: color }}
                                >
                                  {dimFriendlyName}
                                </span>
                                <br />
                                <span className="standard-tooltip__value">
                                  {/* if it's a prediction.. doesn't show value (because there's nothing to show) */}
                                  {isPrediction
                                    ? ""
                                    : formatQueryMetric(
                                        parent,
                                        singleTooltipData.payload[full_key],
                                        globalState.queryMetrics
                                      )}
                                </span>
                              </div>
                            )
                          ) : null;
                        });
                      };

                      return (
                        <div className="standard-tooltip">
                          {tooltipData && (
                            <header className="standard-tooltip__header">
                              <span className="standard-tooltip__key-header">
                                Date:{" "}
                                <strong>
                                  {moment(new Date(currentDatePoint))
                                    .utc()
                                    .format(
                                      formatDate(props.granularity.value, true)
                                    )}
                                </strong>
                              </span>
                            </header>
                          )}

                          <main className="standard-tooltip__module">
                            {props.metrics
                              .filter(metricIsNotAPrediction)
                              .map((metric, j) => {
                                return containsToolTipData(
                                  metric,
                                  seriesFilter,
                                  tooltipData
                                ) ? (
                                  all_dim_keys.length === 0 ? (
                                    <QueryBuilderTooltipMetric
                                      key={j}
                                      metric={metric}
                                      currentTooltipData={
                                        singleTooltipData.payload
                                      }
                                      isPrediction={isPrediction}
                                      color={colors[metric]}
                                      metricFriendlyName={
                                        props.metricMap[metric].friendlyName
                                      }
                                      metrics={globalState.queryMetrics}
                                    />
                                  ) : (
                                    <Fragment key={metric}>
                                      <div className="standard-tooltip__item">
                                        <span
                                          className={clsx(
                                            "standard-tooltip__key",
                                            "text-capitalize"
                                          )}
                                        >
                                          {props.metricMap[metric].friendlyName}
                                        </span>
                                      </div>
                                      {renderItem(0, metric, isPrediction)}
                                    </Fragment>
                                  )
                                ) : null;
                              })}
                          </main>
                          {
                            // in the case that this is a prediction value for a dimension
                            isPrediction && (
                              <QueryBuilderTooltipPrediction
                                tooltipPayload={extractPredictionData(
                                  singleTooltipData
                                )}
                                metrics={globalState.queryMetrics}
                              />
                            )
                          }
                        </div>
                      );
                    }
                  }}
                />
              }
            />

            {allLines.slice(
              0,
              allLines.length > numberOfLines ? numberOfLines : allLines.length
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="col-2">
        <Legend data={legendData} onChangeSelection={onChangeSelection} />
      </div>
    </div>
  );
});

export default QueryBuilderLineChart;
